<template>
  <div class="calculator p-3">
    <div class="mb-3">
      <label class="form-label text-start w-100">Anzahl Sprecher:innen</label>
      <div class="btn-group d-flex" role="group">
        <input
          type="radio"
          class="btn-check"
          :name="`${props.packageType}-speakerCount`"
          :id="`${props.packageType}-speaker1`"
          value="1"
          v-model="numberOfSpeakers"
          @change="updatePrice"
        />
        <label
          class="btn btn-outline-dark"
          :for="`${props.packageType}-speaker1`"
          >1</label
        >
        <input
          type="radio"
          class="btn-check"
          :name="`${props.packageType}-speakerCount`"
          :id="`${props.packageType}-speaker2`"
          value="2"
          v-model="numberOfSpeakers"
          @change="updatePrice"
        />
        <label
          class="btn btn-outline-dark"
          :for="`${props.packageType}-speaker2`"
          >2</label
        >
        <input
          type="radio"
          class="btn-check"
          :name="`${props.packageType}-speakerCount`"
          :id="`${props.packageType}-speaker3`"
          value="3"
          v-model="numberOfSpeakers"
          @change="updatePrice"
        />
        <label
          class="btn btn-outline-dark"
          :for="`${props.packageType}-speaker3`"
          >3</label
        >
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label text-start w-100">Aufnahmelänge auswählen</label>
      <div class="btn-group d-flex" role="group">
        <input
          type="radio"
          class="btn-check"
          :id="`${props.packageType}-duration30`"
          value="30"
          :name="`${props.packageType}-duration`"
          v-model="episodeDuration"
          @change="updatePrice"
        />
        <label
          class="btn btn-outline-dark"
          :for="`${props.packageType}-duration30`"
          >30 min</label
        >
        <input
          type="radio"
          class="btn-check"
          :id="`${props.packageType}-duration60`"
          value="60"
          :name="`${props.packageType}-duration`"
          v-model="episodeDuration"
          @change="updatePrice"
        />
        <label
          class="btn btn-outline-dark"
          :for="`${props.packageType}-duration60`"
          >60 min</label
        >
        <input
          type="radio"
          class="btn-check"
          :id="`${props.packageType}-duration90`"
          value="90"
          :name="`${props.packageType}-duration`"
          v-model="episodeDuration"
          @change="updatePrice"
        />
        <label
          class="btn btn-outline-dark"
          :for="`${props.packageType}-duration90`"
          >90 min</label
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";

const props = defineProps({
  packageType: {
    type: String,
    required: true,
    validator: (value: string) =>
      [
        "audio-pro",
        "audio-pro-hosting",
        "video-basic",
        "video-pro",
        "video-pro-hosting",
      ].includes(value),
  },
});

const emit = defineEmits(["update:modelValue"]);

const numberOfSpeakers = ref(1);
const episodeDuration = ref(30);

// Package configurations
const packages = {
  "audio-pro": {
    basePrice: 140,
    speakerIncrement: 40,
    durationIncrement: 40,
    videoPrice: 0,
  },
  "audio-pro-hosting": {
    basePrice: 190,
    speakerIncrement: 40,
    durationIncrement: 40,
    videoPrice: 0,
  },
  "video-basic": {
    basePrice: 190,
    speakerIncrement: 40,
    durationIncrement: 40,
    videoPrice: 0,
  },
  "video-pro": {
    basePrice: 250,
    speakerIncrement: 50,
    durationIncrement: 50,
    videoPrice: 0,
  },
  "video-pro-hosting": {
    basePrice: 300,
    speakerIncrement: 60,
    durationIncrement: 60,
    videoPrice: 0,
  },
};

const defaultPackage = {
  basePrice: 0,
  speakerIncrement: 0,
  durationIncrement: 0,
  videoPrice: 0,
};

const currentPackage = computed(() => {
  const pkg = packages[props.packageType as keyof typeof packages];
  // Return the package if found, otherwise return default values
  return pkg || defaultPackage;
});

const calculatePrice = () => {
  const pkg = currentPackage.value;
  const speakers = Number(numberOfSpeakers.value) || 1;
  const duration = Number(episodeDuration.value) || 30;

  // Calculate additional cost for speakers
  const speakerCost = Math.max(0, speakers - 1) * (pkg?.speakerIncrement || 0);

  // Calculate additional cost for duration (beyond 30 minutes)
  const durationMultiplier = Math.max(0, duration - 30) / 30;
  const durationCost = durationMultiplier * (pkg?.durationIncrement || 0);

  // Calculate total price
  return (
    (pkg?.basePrice || 0) + speakerCost + durationCost + (pkg?.videoPrice || 0)
  );
};

// Validate package type on component mount
onMounted(() => {
  if (!packages[props.packageType as keyof typeof packages]) {
    console.warn(
      `Invalid package type: ${props.packageType}. Using default values.`,
    );
  }
});

const calculatedPrice = ref(calculatePrice());

// Update the updatePrice function in Calculator.vue
const updatePrice = () => {
  calculatedPrice.value = calculatePrice();
  emit("update:modelValue", {
    packageType: props.packageType, // Add this line
    numberOfSpeakers: Number(numberOfSpeakers.value),
    episodeDuration: Number(episodeDuration.value),
    price: calculatedPrice.value,
  });
};

// Watch for package type changes
watch(
  () => props.packageType,
  () => {
    updatePrice();
  },
);
</script>

<style scoped>
.price-display {
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 0.25rem;
  text-align: center;
}
</style>
